/**
 * Brouillon d'édition d'un modèle de journée — état de formulaire, sans React.
 *
 * Séparé de `TemplateEditor.tsx` parce qu'un module qui exporte des composants
 * ne doit rien exporter d'autre (`react-refresh`), et parce que ces conversions
 * sont du calcul pur : elles se lisent et se relisent mieux ici.
 *
 * ⚠️ Les conversions génériques (chaîne ↔ nombre, `MacroFields`) ont déménagé
 * dans `lib/formFields.ts` à l'étape 6, l'éditeur d'aliment ayant exactement les
 * mêmes besoins. Elles sont réexportées ici : les imports existants n'ont pas
 * bougé.
 *
 * ⚠️ Les quatre cibles restent indépendantes (§4.4) : rien ici ne dérive `kcal`
 * de P/G/L.
 */

import type { DayTemplateDTO, MacroSet } from '../../../../shared/types';
import { newClientId } from '../../lib/dates';
// Conversions génériques, partagées avec l'éditeur d'aliment depuis l'étape 6.
// Réexportées ci-dessous pour que les imports existants ne bougent pas.
import {
  EMPTY_MACROS,
  fieldsOf,
  gapOf,
  macrosOf,
  toField,
  toNumber,
  type MacroFields,
} from '../../lib/formFields';

export { EMPTY_MACROS, fieldsOf, gapOf, macrosOf, toField, toNumber };
export type { MacroFields };

export interface MealDraft extends MacroFields {
  /** Clé de rendu locale — jamais envoyée au serveur, jamais persistée. */
  key: string;
  name: string;
}

/**
 * ⚠️ Pas de cibles de journée ici : **le total est la somme des repas**
 * (`sumMeals`), calculé pour l'affichage et recalculé par le serveur à
 * l'enregistrement. Les stocker dans le brouillon rouvrirait la porte à une
 * saisie parallèle qui diverge du total réel.
 */
export interface TemplateDraft {
  name: string;
  meals: MealDraft[];
}

/** Modèle vierge — les trois repas usuels, à renommer ou supprimer librement. */
export function emptyDraft(): TemplateDraft {
  return {
    name: '',
    meals: [
      { key: newClientId(), name: 'Petit-déjeuner', ...EMPTY_MACROS },
      { key: newClientId(), name: 'Déjeuner', ...EMPTY_MACROS },
      { key: newClientId(), name: 'Dîner', ...EMPTY_MACROS },
    ],
  };
}

export function draftOf(template: DayTemplateDTO): TemplateDraft {
  return {
    name: template.name,
    meals: template.meals.map((meal) => ({
      key: newClientId(),
      name: meal.name,
      ...fieldsOf(meal.targets),
    })),
  };
}

/**
 * Total du modèle : somme, **macro par macro**, des cibles des repas.
 *
 * Reproduit à l'identique ce que fait le serveur (`dayTargetsOf`), pour que le
 * total affiché pendant la saisie soit celui qui sera enregistré. Le serveur
 * reste seul juge (§5.1) : ceci n'est qu'un aperçu.
 */
export function sumMeals(meals: readonly MealDraft[]): MacroSet {
  return meals.reduce<MacroSet>(
    (total, meal) => {
      const macros = macrosOf(meal);
      return {
        protein: total.protein + macros.protein,
        carbs: total.carbs + macros.carbs,
        fat: total.fat + macros.fat,
        kcal: total.kcal + macros.kcal,
      };
    },
    { protein: 0, carbs: 0, fat: 0, kcal: 0 },
  );
}
